/**
 * Snap-to-Edge Utilities for UI Design Canvas
 * Provides alignment detection and snapping for components within artboards
 */

const SNAP_THRESHOLD = 8; // pixels within which snapping occurs

/**
 * Get all snap points for an artboard (edges and center)
 * @param {number} width - Artboard width
 * @param {number} height - Artboard height
 * @returns {Object} Snap points for the artboard
 */
export const getArtboardSnapPoints = (width, height) => ({
    vertical: [0, width / 2, width],     // left, center, right
    horizontal: [0, height / 2, height]  // top, center, bottom
});

/**
 * Get all snap points for a component (edges and center)
 * @param {Object} component - Component with position and dimensions
 * @returns {Object} Snap points for the component
 */
export const getComponentSnapPoints = (component) => {
    const { position_x = 0, position_y = 0, width = 100, height = 40 } = component;
    return {
        vertical: [
            position_x,                    // left edge
            position_x + width / 2,        // center
            position_x + width             // right edge
        ],
        horizontal: [
            position_y,                    // top edge
            position_y + height / 2,       // center
            position_y + height            // bottom edge
        ]
    };
};

/**
 * Calculate all snap targets from sibling components
 * @param {Array} components - All components on the artboard
 * @param {string} excludeId - ID of the component being dragged (exclude from targets)
 * @returns {Object} Arrays of vertical and horizontal snap targets
 */
export const getSnapTargets = (components, excludeId) => {
    const verticalTargets = [];
    const horizontalTargets = [];

    components.forEach(comp => {
        if (comp.id === excludeId) return;

        const snapPoints = getComponentSnapPoints(comp);
        verticalTargets.push(...snapPoints.vertical);
        horizontalTargets.push(...snapPoints.horizontal);
    });

    return { verticalTargets, horizontalTargets };
};

/**
 * Find snap positions for a component being dragged
 * @param {Object} position - Current position { x, y }
 * @param {Object} dimensions - Component dimensions { width, height }
 * @param {Array} siblingComponents - Other components on the artboard
 * @param {number} artboardWidth - Artboard width
 * @param {number} artboardHeight - Artboard height
 * @param {string} componentId - ID of the component being dragged
 * @returns {Object} { position, guides }
 */
export const calculateSnapPosition = (
    position,
    dimensions,
    siblingComponents,
    artboardWidth,
    artboardHeight,
    componentId
) => {
    const { width, height } = dimensions;
    const componentEdges = {
        left: position.x,
        centerX: position.x + width / 2,
        right: position.x + width,
        top: position.y,
        centerY: position.y + height / 2,
        bottom: position.y + height
    };

    // Collect all vertical and horizontal snap targets
    const artboardSnaps = getArtboardSnapPoints(artboardWidth, artboardHeight);
    const siblingSnaps = getSnapTargets(siblingComponents, componentId);

    const allVerticalTargets = [
        ...artboardSnaps.vertical,
        ...siblingSnaps.verticalTargets
    ];
    const allHorizontalTargets = [
        ...artboardSnaps.horizontal,
        ...siblingSnaps.horizontalTargets
    ];

    let snappedX = position.x;
    let snappedY = position.y;
    const guides = { vertical: [], horizontal: [] }; // { position, distance }

    // Check vertical snapping (left, center, right edges)
    const verticalEdges = [
        { edge: componentEdges.left, type: 'left' },
        { edge: componentEdges.centerX, type: 'centerX' },
        { edge: componentEdges.right, type: 'right' }
    ];

    for (const { edge, type } of verticalEdges) {
        for (const target of allVerticalTargets) {
            const distance = Math.abs(edge - target);
            if (distance <= SNAP_THRESHOLD) {
                // Snap to this target
                if (type === 'left') {
                    snappedX = target;
                } else if (type === 'centerX') {
                    snappedX = target - width / 2;
                } else if (type === 'right') {
                    snappedX = target - width;
                }
                // Store guide with component y position for label placement
                guides.vertical.push({
                    position: target,
                    componentTop: position.y,
                    componentBottom: position.y + height
                });
                break; // Only snap to first match per edge
            }
        }
    }

    // Check horizontal snapping (top, center, bottom edges)
    const horizontalEdges = [
        { edge: componentEdges.top, type: 'top' },
        { edge: componentEdges.centerY, type: 'centerY' },
        { edge: componentEdges.bottom, type: 'bottom' }
    ];

    for (const { edge, type } of horizontalEdges) {
        for (const target of allHorizontalTargets) {
            const distance = Math.abs(edge - target);
            if (distance <= SNAP_THRESHOLD) {
                // Snap to this target
                if (type === 'top') {
                    snappedY = target;
                } else if (type === 'centerY') {
                    snappedY = target - height / 2;
                } else if (type === 'bottom') {
                    snappedY = target - height;
                }
                // Store guide with component x position for label placement
                guides.horizontal.push({
                    position: target,
                    componentLeft: position.x,
                    componentRight: position.x + width
                });
                break; // Only snap to first match per edge
            }
        }
    }

    return {
        position: { x: snappedX, y: snappedY },
        guides
    };
};

export const SNAP_GUIDE_COLOR = '#ff6b6b';
export { SNAP_THRESHOLD };
